import { buildQueryString } from './buildQueryString';
import deduplicator from './deduplicator';
import { getApiUrl } from './getApiUrl';
import { handleBadRequest } from './handleBadRequest';
import { methodSupportsBody } from './methodSupportsBody';
import { request } from './request';
import { type ApiOptions } from './types';
import { Sentry } from '~shared/sentry';

/**
 * Utility function for handling auto-generated API endpoint requests
 *
 * @example
 *
 * ```ts
 * // Execute an async API request
 * const options = await AvailabilityOptionsEndpoint(apiEndpointHandler).fetchAll(params);
 * ```
 *
 * @example
 *
 * ```tsx
 * // Execute an API request using React Query
 * const availabilityOptionsEndpoint = AvailabilityOptionsEndpoint(apiEndpointHandler);
 * const { data, isError, isPending } = useQuery({
 *   queryKey: ['availability-options', params],
 *   queryFn: () => availabilityOptionsEndpoint.fetchAll(params),
 * });
 * ```
 *
 * @see {@link https://tanstack.com/query/v4/docs/overview | React Query Documentation}
 * @todo Create/link Notion document for more context
 */
export const apiEndpointHandler = async <T = unknown>(
  method: string,
  endpoint: string,
  params: Record<string, unknown> = {},
  { version, splitArrayParams }: ApiOptions = {},
): Promise<T> => {
  const hasBody = methodSupportsBody(method);
  const query = hasBody ? '' : buildQueryString(params, splitArrayParams);
  const body = hasBody ? JSON.stringify(params) : undefined;
  // if there is a specified version, use it. Otherwise, the endpoint includes the version
  const url = version ? getApiUrl(version, endpoint, query) : getApiUrl(endpoint, query);

  const requestFn = async () => {
    // Apart from basic 404 logging and 401 redirects, error handling is left for the implementer to handle
    return request(url, { method, body })
      .then((response) => {
        handleBadRequest({ response, url, method, body });

        return response.json();
      })
      .catch((error: unknown) => {
        // We should not treat 'Network request failed' errors as an uncaught exception.
        // 'Network request failed' is an expected error that can occur when a user loses internet, closes the app, etc.
        if ((error as Error).message !== 'Network request failed') {
          Sentry.captureException(error instanceof Error ? error : new Error('Error with api request'), {
            contexts: { error: { error }, url: { url }, method: { method } },
          });
        }

        // eslint-disable-next-line promise/no-return-wrap
        return Promise.reject(error as Error);
      });
  };

  if (method === 'GET') {
    return deduplicator(url, requestFn);
  } else {
    return requestFn();
  }
};
